import React from "react";
import StatCard, { StatValue } from "./StatCard";
import {
  PurchasesIcon,
  CartIcon,
  RevenueIcon,
  CreditsIcon,
  EntitlementIcon,
  RoundStatusIcon,
} from "./RoundIcons";
import { ROUNDS, TOKEN_BREAKDOWN } from "./staticData";

const fmt = (n, dec = 2) =>
  n != null ? Number(n).toLocaleString("en-US", { maximumFractionDigits: dec }) : "0";

const fmtUsd = (n) => (n != null ? `$${fmt(n)}` : "$0.00");

// unix seconds (from the presale contract) -> "Sep 10, 2026"
const fmtRoundDate = (seconds) => {
  if (!seconds) return null;
  return new Date(Number(seconds) * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// `stats` is the raw `data.data.data` object from /users/special/stats:
// { totalDocuments, stats: [{ tokenPurchased, totalEth, totalUsdt, ..., totalSaleConverted }] }
const RoundStats = ({
  stats,
  loading,
  round,
  roundLoading,
  roundMeta = ROUNDS[0],
}) => {
  const agg = stats?.stats?.[0] || {};
  const purchases = stats?.totalDocuments;
  const dash = loading || !stats ? "—" : null;

  // On the x2 bonus round the API's tokenPurchased already carries the doubled
  // amount: it is the entitlement, and the tokens actually bought are half of
  // it (the other half being the bonus).
  const entitlement = agg.tokenPurchased;
  const tokenPurchased =
    roundMeta.showEntitlement && entitlement != null
      ? entitlement / 2
      : entitlement;

  const revenue =
    agg.totalSaleConverted != null ? agg.totalSaleConverted * 0.98 : null;

  // credits are unlocked 1:1 with the converted sale amount; prefer an explicit
  // creditsUnlocked aggregate if the API provides one.
  const totalCredits =
    agg.creditsUnlocked != null ? agg.creditsUnlocked : agg.totalSaleConverted;
  const avgCredits =
    totalCredits != null && purchases ? totalCredits / purchases : null;

  // round status / dates come from the contract (rounds(index)) — if that read
  // is still in flight, failed, or returned nothing, show a dash rather than a
  // stale hardcoded value.
  const roundDash = roundLoading || !round;
  const roundStatus = roundDash ? "—" : round.status;
  const roundStart = (!roundDash && fmtRoundDate(round.startTime)) || "—";
  const roundEnd = (!roundDash && fmtRoundDate(round.endTime)) || "—";
  const roundNotes = [`Start: ${roundStart}`, `End: ${roundEnd}`];

  const breakdown = TOKEN_BREAKDOWN.filter(
    ({ field }) => Number(agg[field]) > 0,
  ).map(({ field, unit }) => (
    <>
      {fmt(agg[field], 4)} <span className="smalltext">{unit}</span>
    </>
  ));

  return (
    <div className="roundcards">
      <StatCard icon={<PurchasesIcon />} label="No. of Purchases">
        <h6 className="cardhead">{dash ?? fmt(purchases, 0)}</h6>
      </StatCard>

      <StatCard
        icon={
          <img
            src="/assets/kai.svg"
            alt="KAI"
            width="24"
            height="24"
            className="cardimg"
          />
        }
        label="KAI Purchased"
      >
        <StatValue value={dash ?? fmt(tokenPurchased, 2)} unit="KAI" />
      </StatCard>

      {roundMeta.showEntitlement && (
        <StatCard
          icon={<EntitlementIcon />}
          label="KAI Entitlement × 2"
          notes={
            dash
              ? []
              : [
                  "Purchased ×2 bonus multiplier",
                  `Bonus tokens ${fmt(tokenPurchased, 2)}`,
                ]
          }
        >
          <StatValue value={dash ?? fmt(entitlement, 2)} unit="KAI" />
        </StatCard>
      )}

      <StatCard
        icon={<CartIcon />}
        label="Purchased Amount"
        notes={dash ? [] : breakdown}
      >
        <h6 className="cardhead">{dash ?? fmtUsd(agg.totalSaleConverted)}</h6>
      </StatCard>

      <StatCard icon={<RevenueIcon />} label="Platform Net Revenue (98%)">
        <h6 className="cardhead">{dash ?? fmtUsd(revenue)}</h6>
      </StatCard>

      {roundMeta.showCreditsUnlocked && (
        <StatCard
          icon={<CreditsIcon />}
          label="Eligible Credits Open to Use"
          notes={
            dash
              ? []
              : [
                  "1:1 vs. investment, capped by balance",
                  `Avg ${fmt(avgCredits, 0)} / buyer`,
                ]
          }
        >
          <StatValue value={dash ?? fmt(totalCredits, 0)} unit="credits" />
        </StatCard>
      )}

      <StatCard
        icon={<RoundStatusIcon />}
        label="Round Status"
        notes={roundNotes}
      >
        <h6 className="cardhead">{roundStatus}</h6>
      </StatCard>
    </div>
  );
};

export default RoundStats;
